import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Crown,
  X,
  ArrowRight,
  KeyRound,
  ShieldCheck,
  Eye,
  EyeOff,
  Compass,
  Download,
  Lock,
  FileText,
  BookOpen,
  Sparkles,
} from "lucide-react";

import DashboardLayout from "../components/layout/DashboardLayout";
import { useAuth } from "../context/AuthContext";
import { useOnboardingStore } from "../store/useOnboardingStore";
import { api, formatApiError } from "../lib/api";
import DateField from "../components/ui/DateField";
import { toast } from "sonner";

const USAGE_LABELS = {
  assets: "Assets",
  liabilities: "Liabilities",
  insurance: "Insurance",
  goals: "Goals",
  documents: "Documents",
  shared_members: "Shared members",
  nominees: "Nominees",
};
const USAGE_ORDER = [
  "assets",
  "liabilities",
  "insurance",
  "goals",
  "documents",
  "shared_members",
  "nominees",
];

export default function Settings() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const resetTour = useOnboardingStore((s) => s.resetTour);

  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  const isPremium = (user?.plan || "free").toLowerCase() !== "free";

  // Phone add/edit
  const [phoneEditing, setPhoneEditing] = useState(false);
  const [phoneVal, setPhoneVal] = useState("");
  const [phoneSaving, setPhoneSaving] = useState(false);

  const openPhoneEdit = () => {
    setPhoneVal(user?.phone || "");
    setPhoneEditing(true);
  };

  const savePhone = async () => {
    setPhoneSaving(true);
    try {
      await api.patch("/auth/profile", { phone: phoneVal.trim() || null });
      await refresh();
      toast.success(
        phoneVal.trim() ? "Phone number saved" : "Phone number removed",
      );
      setPhoneEditing(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPhoneSaving(false);
    }
  };

  // Date of birth add/edit (drives retirement age + corpus)
  const [dobEditing, setDobEditing] = useState(false);
  const [dobVal, setDobVal] = useState("");
  const [dobSaving, setDobSaving] = useState(false);

  const openDobEdit = () => {
    setDobVal(
      user?.date_of_birth ? String(user.date_of_birth).slice(0, 10) : "",
    );
    setDobEditing(true);
  };

  const saveDob = async () => {
    if (!dobVal) {
      toast.error("Please pick a date");
      return;
    }
    setDobSaving(true);
    try {
      await api.patch("/auth/profile", { date_of_birth: dobVal });
      await refresh();
      toast.success("Date of birth saved — retirement plan updated");
      setDobEditing(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDobSaving(false);
    }
  };

  React.useEffect(() => {
    api
      .get("/subscription/usage")
      .then((r) => setUsage(r.data))
      .catch(() => {});
  }, []);

  const downloadBlob = async (url, filename, type) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(
        new Blob([res.data], { type }),
      );
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadCSV = (kind) =>
    downloadBlob(`/export/${kind}.csv`, `${kind}.csv`, "text/csv");
  const downloadPDF = () =>
    downloadBlob(
      "/reports/networth.pdf",
      "accesswealth-networth-report.pdf",
      "application/pdf",
    );

  const replayTour = async () => {
    setBusy(true);
    try {
      await api.patch("/auth/onboarding", { has_completed_onboarding: false });
      resetTour("dashboard");
      await refresh();
      toast.success("Starting the product tour…");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const [registered, setRegistered] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const registerInterest = async (planType) => {
    setBusy(true);

    try {
      await api.post("/subscription/register-interest", {
        plan_type: planType,
      });

      setRegistered(true);

      toast.success("You're on the early access list 🚀");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downgrade = async () => {
    if (
      !window.confirm(
        "Downgrade to Free? You'll keep your data but lose unlimited assets and advanced features.",
      )
    ) {
      return;
    }

    setBusy(true);

    try {
      await api.post("/subscription/downgrade");

      await refresh();

      toast.success("Downgraded to Free");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-slate-500">
          Your account
        </p>

        <h1
          data-testid="settings-title"
          className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
        >
          Settings
        </h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* PROFILE */}

        <section className="card-soft p-6 lg:col-span-2">
          <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">
            Profile
          </h2>

          <div className="mt-5 flex items-center gap-4">
            <div className="grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-emerald-500 to-blue-600 text-white font-display font-bold text-2xl">
              {user?.name?.[0]?.toUpperCase()}
            </div>

            <div>
              <div className="font-display font-bold text-slate-900 dark:text-white text-lg">
                {user?.name}
              </div>

              <div className="text-sm text-slate-500">{user?.email}</div>

              <div className="mt-1 inline-flex items-center gap-1.5">
                <span
                  className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ${
                    user?.plan === "premium"
                      ? "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                  }`}
                >
                  {user?.plan?.toUpperCase()}
                </span>

                {user?.role === "admin" && (
                  <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300">
                    ADMIN
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-4">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">
                  Date of Birth
                </div>
                {!dobEditing && (
                  <button
                    onClick={openDobEdit}
                    data-testid="edit-dob"
                    className="text-xs font-semibold text-emerald-600 hover:underline"
                  >
                    {user?.date_of_birth ? "Edit" : "Add"}
                  </button>
                )}
              </div>
              {dobEditing ? (
                <div className="mt-2 flex items-center gap-2">
                  <DateField
                    className="flex-1"
                    value={dobVal}
                    max={new Date().toISOString().slice(0, 10)}
                    toYear={new Date().getFullYear()}
                    onChange={(v) => setDobVal(v)}
                  />
                  <button
                    onClick={saveDob}
                    disabled={dobSaving}
                    className="btn-primary !text-xs !py-2 !px-3"
                  >
                    {dobSaving ? "Saving…" : "Save"}
                  </button>
                  <button
                    onClick={() => setDobEditing(false)}
                    className="btn-secondary !text-xs !py-2 !px-3"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="mt-1.5 font-semibold text-slate-900 dark:text-white">
                  {user?.date_of_birth
                    ? new Date(user.date_of_birth).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "—"}
                </div>
              )}
              <p className="mt-2 text-[11px] text-slate-500">
                Drives your retirement age &amp; corpus.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-500">
                Current Age
              </div>
              <div className="mt-1.5 font-semibold text-slate-900 dark:text-white">
                {user?.current_age != null ? `${user.current_age} yrs` : "—"}
              </div>
            </div>

            {/* PHONE (add / edit) */}
            <div className="col-span-2 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-4">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">
                  Phone Number
                </div>
                {!phoneEditing && (
                  <button
                    onClick={openPhoneEdit}
                    data-testid="edit-phone"
                    className="text-xs font-semibold text-emerald-600 hover:underline"
                  >
                    {user?.phone ? "Edit" : "Add"}
                  </button>
                )}
              </div>
              {phoneEditing ? (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="tel"
                    maxLength={10}
                    value={phoneVal}
                    onChange={(e) =>
                      setPhoneVal(
                        e.target.value.replace(/\D/g, "").slice(0, 10),
                      )
                    }
                    placeholder="9876543210"
                    className="input-soft flex-1"
                  />
                  <button
                    onClick={savePhone}
                    disabled={phoneSaving}
                    className="btn-primary !text-xs !py-2 !px-3"
                  >
                    {phoneSaving ? "Saving…" : "Save"}
                  </button>
                  <button
                    onClick={() => setPhoneEditing(false)}
                    className="btn-secondary !text-xs !py-2 !px-3"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="mt-1.5 font-semibold text-slate-900 dark:text-white">
                  {user?.phone || "Not added"}
                </div>
              )}
              <p className="mt-2 text-[11px] text-slate-500">
                Helps family members find and link to you. Leave blank to
                remove.
              </p>
            </div>
          </div>
        </section>

        {/* PLAN CARD */}

        <section
          data-testid="plan-card"
          className={`card-soft p-6 ${
            user?.plan === "premium" ? "ring-2 ring-emerald-500" : ""
          }`}
        >
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-emerald-600" />

            <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">
              {user?.plan === "premium" ? "Premium plan" : "Free plan"}
            </h2>
          </div>

          {user?.plan === "premium" ? (
            <>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                You're on Premium — unlimited assets, advanced projections,
                inheritance planning, and an ad-free experience.
              </p>

              <button
                data-testid="downgrade-btn"
                onClick={downgrade}
                disabled={busy}
                className="btn-secondary w-full mt-4 !text-xs"
              >
                Downgrade to Free
              </button>
            </>
          ) : (
            <>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                Unlock unlimited assets, advanced projections, account
                aggregator integration, inheritance planning, AI insights, and
                an ad-free experience.
              </p>

              <div className="mt-4">
                {/* <div className="num-mono text-3xl font-bold text-slate-900 dark:text-white">
                  ₹499
                  <span className="ml-1 text-sm font-normal text-slate-500">
                    /month
                  </span>
                </div> */}

                <div className="mt-2 inline-flex items-center rounded-full bg-emerald-100 dark:bg-emerald-500/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                  Launching Soon
                </div>
              </div>

              <button
                data-testid="upgrade-btn"
                onClick={() => setShowPremiumModal(true)}
                className="btn-primary w-full mt-5"
              >
                Register & Get Early Access
              </button>
            </>
          )}
        </section>

        {/* PLAN USAGE (free tier only) */}
        {usage && !usage.is_premium && (
          <section className="card-soft p-6 lg:col-span-3">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">
                  Plan usage
                </h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  You're on the Free plan. Here's how much of each limit you've
                  used.
                </p>
              </div>
              <button
                onClick={() => setShowPremiumModal(true)}
                className="btn-primary !text-xs whitespace-nowrap"
              >
                <Crown className="h-4 w-4" /> Go unlimited
              </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {USAGE_ORDER.map((k) => {
                const u = usage.usage[k];
                if (!u) return null;
                const limit = u.limit;
                const pct = limit ? Math.min(100, (u.used / limit) * 100) : 0;
                const atLimit = limit != null && u.used >= limit;
                return (
                  <div key={k}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-slate-700 dark:text-slate-300">
                        {USAGE_LABELS[k]}
                      </span>
                      <span
                        className={
                          atLimit
                            ? "text-rose-600 font-semibold"
                            : "text-slate-400"
                        }
                      >
                        {u.used} / {limit ?? "∞"}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          atLimit
                            ? "bg-rose-500"
                            : pct > 80
                              ? "bg-amber-500"
                              : "bg-emerald-500"
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* DATA EXPORT (premium) */}
        <section className="card-soft p-6 lg:col-span-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                Data export
                <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-blue-600 text-white">
                  Premium
                </span>
              </h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 max-w-2xl">
                Download your data as CSV — open it in Excel or Google Sheets,
                or keep an offline backup.
              </p>
            </div>
            {!isPremium && <Lock className="h-5 w-5 text-slate-400 shrink-0" />}
          </div>

          {isPremium ? (
            <div className="mt-4 space-y-3">
              <button onClick={downloadPDF} className="btn-primary !text-xs">
                <FileText className="h-3.5 w-3.5" /> Net Worth Report (PDF)
              </button>
              <div className="flex flex-wrap gap-2">
                {[
                  ["assets", "Assets"],
                  ["liabilities", "Liabilities"],
                  ["goals", "Goals"],
                  ["insurance", "Insurance"],
                ].map(([kind, label]) => (
                  <button
                    key={kind}
                    onClick={() => downloadCSV(kind)}
                    className="btn-secondary !text-xs"
                  >
                    <Download className="h-3.5 w-3.5" /> {label} CSV
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowPremiumModal(true)}
              className="btn-primary !text-xs mt-4"
            >
              <Crown className="h-4 w-4" /> Unlock CSV export
            </button>
          )}
        </section>

        {/* PASSWORD */}

        <section className="card-soft p-6 lg:col-span-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">
                Password & Security
              </h2>

              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                Change your account password anytime.
              </p>
            </div>

            <button
              onClick={() => setShowPasswordModal(true)}
              className="btn-primary"
            >
              <KeyRound className="h-4 w-4" />
              Change Password
            </button>
          </div>
        </section>

        {/* ACCESS MANAGEMENT — Personal Finance Advisors */}
        <section className="card-soft p-6 lg:col-span-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">
                Access Management
              </h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                Connect with a Personal Finance Advisor, control what you share,
                review change requests, and revoke access anytime.
              </p>
            </div>

            <button
              onClick={() => navigate("/dashboard/access")}
              data-testid="settings-access-management"
              className="btn-primary whitespace-nowrap"
            >
              <ShieldCheck className="h-4 w-4" />
              Manage Access
            </button>
          </div>
        </section>

        {/* GUIDE & TIPS */}

        <section className="relative overflow-hidden card-soft lg:col-span-3 p-0">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-blue-500/5 to-transparent" />
          <div className="relative p-6 flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-600 text-white">
                <BookOpen className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">
                    Guide & tips
                  </h2>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold px-2 py-0.5">
                    <Sparkles className="h-3 w-3" /> New
                  </span>
                </div>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 max-w-2xl">
                  A friendly manual for every part of AccessWealth — what each
                  section does, how to use it, and pro tips. Assets, goals,
                  taxation, sharing and more, in plain English.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/dashboard/guide")}
              data-testid="settings-open-guide"
              className="btn-primary whitespace-nowrap"
            >
              <BookOpen className="h-4 w-4" /> Open guide
            </button>
          </div>
        </section>

        {/* PRODUCT TOUR */}

        <section className="card-soft p-6 lg:col-span-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600">
                <Compass className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">
                  Product tour
                </h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 max-w-2xl">
                  New here or want a refresher? Replay the guided dashboard tour
                  to see how net worth, projections and milestones work.
                </p>
              </div>
            </div>

            <button
              onClick={replayTour}
              disabled={busy}
              className="btn-secondary whitespace-nowrap"
            >
              <Compass className="h-4 w-4" />
              Replay tour
            </button>
          </div>
        </section>

        {/* DELETE ACCOUNT */}

        <section className="card-soft p-6 lg:col-span-3 border border-rose-200 dark:border-rose-500/20 bg-rose-50/50 dark:bg-rose-500/5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-display text-lg font-bold text-rose-700 dark:text-rose-300">
                Delete Account
              </h2>

              <p className="mt-1 text-sm text-rose-700/80 dark:text-rose-300/80 max-w-2xl">
                Permanently delete your AccessWealth account and all associated
                data including assets, liabilities, goals, inheritance
                allocations, nominees, analytics and vault information.
              </p>

              <div className="mt-4 rounded-xl border border-rose-200 dark:border-rose-500/20 bg-white dark:bg-slate-900 p-4">
                <div className="text-xs font-semibold uppercase tracking-widest text-rose-600">
                  Warning
                </div>

                <div className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                  This action is irreversible and cannot be undone.
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 transition"
            >
              Delete Account
            </button>
          </div>
        </section>

        {/* SECURITY */}

        {/* <section className="card-soft p-6 lg:col-span-3">
          <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">
            Security
          </h2>

          <div className="mt-5 grid sm:grid-cols-3 gap-4">
            <SecurityRow
              icon={KeyRound}
              title="Password"
              status="Strong"
              detail="Last changed: never"
            />

            <SecurityRow
              icon={Smartphone}
              title="2FA"
              status="Not enabled"
              detail="Add an extra layer of safety"
              highlight
            />

            <SecurityRow
              icon={BellRing}
              title="Login alerts"
              status="On"
              detail="Email when new device signs in"
            />
          </div>
        </section> */}

        {/* PRIVACY */}

        <section className="card-soft p-6 lg:col-span-3 bg-gradient-to-br from-emerald-500/5 to-blue-500/5">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-6 w-6 text-emerald-600 flex-shrink-0" />

            <div>
              <h3 className="font-display font-bold text-slate-900 dark:text-white">
                Your data is yours
              </h3>

              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 max-w-2xl">
                All sensitive fields are encrypted .
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* PREMIUM MODAL */}

      {showPremiumModal && (
        <PremiumModal
          user={user}
          busy={busy}
          registered={registered}
          onClose={() => {
            setShowPremiumModal(false);
            setRegistered(false);
          }}
          onSubmit={registerInterest}
        />
      )}
      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
      )}
      {showDeleteModal && (
        <DeleteAccountModal onClose={() => setShowDeleteModal(false)} />
      )}
    </DashboardLayout>
  );
}

function PremiumModal({ user, onClose, onSubmit, busy, registered }) {
  const [selectedPlan, setSelectedPlan] = React.useState("premium_monthly");

  const plans = [
    {
      key: "premium_monthly",

      name: "Premium Monthly",

      // price: "₹199/month",

      description:
        "Flexible monthly premium access with unlimited wealth tracking.",
    },

    {
      key: "premium_yearly",

      name: "Premium Yearly",

      // price: "₹1,799/year",

      badge: "BEST VALUE",

      description: "Save more yearly and unlock every premium feature.",
    },

    {
      key: "family_yearly",

      name: "Family Yearly",

      // price: "₹2,999/year",

      badge: "FAMILY",

      description:
        "Premium access for up to 4 family members with Family Vault.",
    },
  ];

  const features = [
    "Unlimited assets & liabilities",
    "Unlimited Goals",
    "Unlimited document upload",
    "Unlimited nominees",
    "Unlimited Family Sharing",
    "PDF & CSV exports",
    "Ad-free experience",
    "Priority support",
    "Account Aggregator integration",
    "AI-Insights",
  ];

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="w-full max-w-3xl rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden">
          {/* HEADER */}

          <div className="bg-gradient-to-r from-emerald-500 to-blue-600 p-8 text-white">
            <div className="flex items-start justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-[11px] uppercase tracking-widest font-bold">
                  <Crown className="h-3 w-3" />
                  Premium Early Access
                </div>

                <h2 className="mt-4 font-display text-3xl font-bold tracking-tight">
                  AccessWealth Premium
                </h2>

                <p className="mt-2 text-sm text-white/80 max-w-xl">
                  Choose your preferred subscription plan and get early access
                  once payments launch.
                </p>
              </div>

              <button
                onClick={onClose}
                className="grid h-9 w-9 place-items-center rounded-xl hover:bg-white/10"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {!registered ? (
            <div className="p-8">
              {/* PLAN SELECTION */}

              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-4">
                  Select your preferred plan
                </div>

                <div className="grid gap-4">
                  {plans.map((plan) => {
                    const active = selectedPlan === plan.key;

                    return (
                      <button
                        key={plan.key}
                        type="button"
                        onClick={() => setSelectedPlan(plan.key)}
                        className={`relative rounded-2xl border p-5 text-left transition-all ${
                          active
                            ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 ring-2 ring-emerald-500/20"
                            : "border-slate-200 dark:border-slate-800 hover:border-emerald-300"
                        }`}
                      >
                        {plan.badge && (
                          <div className="absolute right-4 top-4 rounded-full bg-amber-400 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-slate-900">
                            {plan.badge}
                          </div>
                        )}

                        <div className="flex items-start gap-4">
                          <div
                            className={`mt-1 h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                              active ? "border-emerald-500" : "border-slate-300"
                            }`}
                          >
                            {active && (
                              <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                            )}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center justify-between gap-4">
                              <div>
                                <div className="font-display text-lg font-bold text-slate-900 dark:text-white">
                                  {plan.name}
                                </div>

                                <div className="mt-1 text-sm text-slate-500">
                                  {plan.description}
                                </div>
                              </div>

                              <div className="text-right">
                                <div className="num-mono text-2xl font-bold text-slate-900 dark:text-white">
                                  {plan.price}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* FEATURES */}

              <div className="mt-8">
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-4">
                  Included Features
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {features.map((feature) => (
                    <div
                      key={feature}
                      className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className="rounded-full bg-emerald-100 dark:bg-emerald-500/10 p-1">
                          <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                        </div>

                        <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                          {feature}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* EMAIL */}

              <div className="mt-8 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-5">
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  Registering interest for
                </div>

                <div className="mt-1 font-semibold text-slate-900 dark:text-white">
                  {user?.email}
                </div>
              </div>

              {/* BUTTONS */}

              <div className="mt-8 flex gap-3">
                <button onClick={onClose} className="btn-secondary flex-1">
                  Cancel
                </button>

                <button
                  onClick={() => onSubmit(selectedPlan)}
                  disabled={busy}
                  className="btn-primary flex-1"
                >
                  {busy ? "Registering..." : "Register Interest"}

                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="p-10 text-center">
              <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-emerald-100 dark:bg-emerald-500/10">
                <ShieldCheck className="h-10 w-10 text-emerald-600" />
              </div>

              <h3 className="mt-6 font-display text-2xl font-bold text-slate-900 dark:text-white">
                You're on the early access list 🚀
              </h3>

              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 max-w-md mx-auto">
                We'll notify you once subscriptions and payments go live.
              </p>

              <button onClick={onClose} className="btn-primary mt-8">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SecurityRow({ icon: Icon, title, status, detail, highlight }) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        highlight
          ? "border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10"
          : "border-slate-100 dark:border-slate-800"
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-600 dark:text-slate-300" />

        <span className="font-semibold text-sm">{title}</span>
      </div>

      <div className="mt-2 text-xs font-bold text-slate-900 dark:text-white">
        {status}
      </div>

      <div className="text-[11px] text-slate-500 mt-0.5">{detail}</div>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [saving, setSaving] = useState(false);

  const [showCurrent, setShowCurrent] = useState(false);

  const [showNew, setShowNew] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);

  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const onSubmit = async (e) => {
    e.preventDefault();

    if (form.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");

      return;
    }

    if (form.new_password !== form.confirm_password) {
      toast.error("Passwords do not match");

      return;
    }

    setSaving(true);

    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,

        new_password: form.new_password,
      });

      toast.success("Password updated successfully");

      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden">
          {/* HEADER */}

          <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800">
            <div>
              <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">
                Change Password
              </h2>

              <p className="mt-1 text-xs text-slate-500">
                Keep your AccessWealth account secure.
              </p>
            </div>

            <button
              onClick={onClose}
              className="grid h-9 w-9 place-items-center rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* BODY */}

          <form onSubmit={onSubmit} className="p-6 space-y-4">
            {/* CURRENT */}

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Current Password
              </label>

              <div className="relative">
                <input
                  type={showCurrent ? "text" : "password"}
                  required
                  value={form.current_password}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      current_password: e.target.value,
                    })
                  }
                  className="input-soft pr-10"
                  placeholder="Current password"
                />

                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showCurrent ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* NEW */}

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                New Password
              </label>

              <div className="relative">
                <input
                  type={showNew ? "text" : "password"}
                  required
                  minLength={6}
                  value={form.new_password}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      new_password: e.target.value,
                    })
                  }
                  className="input-soft pr-10"
                  placeholder="New password"
                />

                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showNew ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* CONFIRM */}

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Confirm Password
              </label>

              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  required
                  value={form.confirm_password}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      confirm_password: e.target.value,
                    })
                  }
                  className="input-soft pr-10"
                  placeholder="Confirm password"
                />

                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showConfirm ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* ACTIONS */}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={saving}
                className="btn-primary flex-1"
              >
                {saving ? "Updating..." : "Update Password"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function DeleteAccountModal({ onClose }) {
  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    confirmation: "",
    password: "",
  });

  const onSubmit = async (e) => {
    e.preventDefault();

    if (form.confirmation !== "DELETE") {
      toast.error("Please type DELETE to continue");

      return;
    }

    setLoading(true);

    try {
      await api.post("/auth/delete", {
        confirmation: form.confirmation,
        password: form.password,
      });

      onClose();

      toast.success("Account deleted successfully");

      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden">
          {/* HEADER */}

          <div className="border-b border-slate-200 dark:border-slate-800 p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-rose-600">
                  Dangerous Action
                </div>

                <h2 className="mt-2 font-display text-2xl font-bold text-slate-900 dark:text-white">
                  Delete Account
                </h2>

                <p className="mt-2 text-sm text-slate-500">
                  This permanently deletes all your AccessWealth data.
                </p>
              </div>

              <button
                onClick={onClose}
                className="grid h-9 w-9 place-items-center rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* BODY */}

          <form onSubmit={onSubmit} className="p-6 space-y-5">
            <div className="rounded-2xl border border-rose-200 dark:border-rose-500/20 bg-rose-50 dark:bg-rose-500/10 p-4">
              <div className="text-sm font-semibold text-rose-700 dark:text-rose-300">
                This will permanently remove:
              </div>

              <ul className="mt-3 space-y-1 text-sm text-rose-700/80 dark:text-rose-300/80">
                <li>• Assets & liabilities</li>
                <li>• Goals & analytics</li>
                <li>• Family Vault</li>
                <li>• Nominees & inheritance allocations</li>
                <li>• Saved calculations</li>
              </ul>
            </div>

            {/* CONFIRMATION */}

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Type DELETE to confirm
              </label>

              <input
                required
                value={form.confirmation}
                onChange={(e) =>
                  setForm({
                    ...form,
                    confirmation: e.target.value,
                  })
                }
                className="input-soft"
                placeholder="DELETE"
              />
            </div>

            {/* PASSWORD */}

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Current Password
              </label>

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={form.password}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      password: e.target.value,
                    })
                  }
                  className="input-soft pr-10"
                  placeholder="Current password"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* ACTIONS */}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading}
                className="flex-1 rounded-xl bg-rose-600 px-4 py-3 text-sm font-semibold text-white hover:bg-rose-700 transition disabled:opacity-50"
              >
                {loading ? "Deleting..." : "Delete Permanently"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
